"use client";

import { create } from "zustand";
import { AccountType, TokenContract } from "@shared/src/lib/types";
import _chains, { IxChain } from "@shared/src/config/chains";
import { Network } from "@sdk/shared/types/config.types";
import { alchemyChains } from "@shared/src/config/alchemy";
import chains from "@shared/src/config/chains";
import { CHAIN_ID_KEY } from "@shared/src/lib/queryKeys";
import { getAccount, switchChain, SwitchChainNotSupportedError } from "@wagmi/core";
import { wagmiConfig } from "@shared/src/config/wagmiConfig";
import { ixToast } from "@shared/src/utils/ixToast";
import { handleSwitch } from "@shared/src/lib/handleSwitch";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { SwitchChainError } from "viem";
import { useHostStore } from "./useHostStore";

interface InitializeArgs {
  chains: IxChain[];
  networks: Network[];
}

interface ContractsState {
  tokens: TokenContract[];
  networks: Network[] | null;
  activeChain: IxChain | undefined | null;
  isCorrectChain: boolean;
  chains: IxChain[];
  changeChain: (chainId: number, accountType: AccountType) => Promise<void>;
  setActiveChain: (chain: IxChain | null) => void;
  initialize: (initialiseArgs: InitializeArgs) => Promise<void>;
  unSubscribe: () => void;
  isChangingChain: boolean;
  setIsChangingChain: (isChangingChain: boolean) => void;
  isInitialised: boolean;
}

export const useBlockchainStore = create<ContractsState>((set, get) => ({
  isInitialised: false,
  p2pContract: null,
  tokens: [],
  writeContract: null,
  activeChain: null,
  isCorrectChain: false,
  networks: null,
  chains: _chains,
  isChangingChain: false,
  setIsChangingChain: isChangingChain => {
    set({ isChangingChain });
  },
  changeChain: async (chainId: number, accountType: AccountType) => {
    const store = get();
    const showSwitchingNotSupportedModal = () => {
      const modal = useModalStore.getState();
      modal.showModal({
        type: "info",
        title: "Manual Switch Required",
        buttonText: "Ok",
        buttonClick: () => modal.closeModal(),
        messageTitle:
          "The connected wallet does not support switching chains from within the Dapp, please switch from within your wallet",
      });
    };
    try {
      const chain = (accountType === "sca" ? alchemyChains : chains).find(c => c.id === chainId);
      if (!chain) throw "unsupported chain";
      store.setIsChangingChain(true);
      const res = await switchChain(wagmiConfig, {
        chainId,
        addEthereumChainParameter: {
          iconUrls: [chain.iconUrl],
        },
      });
      console.log("chain switched", { res, chainId });
      if (accountType === "sca") {
        localStorage.setItem(CHAIN_ID_KEY, chainId.toString());
      } else {
        console.log("switching wagmi chain", { chainId, accountType });
        const wagmiAccount = getAccount(wagmiConfig);
        const currentChainId = wagmiAccount.chainId;
        if (currentChainId !== chainId) {
          console.log("direct switching chain", { currentChainId, chainId, chain });
          await handleSwitch(chain);
          const newChainId = getAccount(wagmiConfig).chainId;
          if (newChainId !== chainId) {
            showSwitchingNotSupportedModal();
          }
        }
      }

      store.setActiveChain(chain || null);
    } catch (error: any) {
      if (error instanceof SwitchChainError || error instanceof SwitchChainNotSupportedError) {
        showSwitchingNotSupportedModal();
      } else {
        ixToast.error(error);
      }
      console.error("Chain switch failed:", error);
    } finally {
      store.setIsChangingChain(false);
    }
  },
  setActiveChain: chain => {
    useHostStore.getState().setAndSwitchChain(chain?.id || null);
    // if (!currentNetwork) throw "cant find network to set";
    set({
      activeChain: chain,
      isCorrectChain: !!chain,
    });
  },

  initialize: async ({ chains, networks }): Promise<void> => {
    set({ chains, networks, isInitialised: true });
  },
  unSubscribe: () => {},
}));
